/**
 * LifecycleDataPage — the "Event Lifecycle Data" tab.
 *
 * Route: `/event/lifecycle-data`
 *
 * The content is an embedded Octopus QuickSight dashboard, not charts built here. The
 * funnel (nomination → registration → attendee → MQL → SQL → pipeline) and the By-BU
 * breakdown are Octopus's data and Octopus's dashboard; this page's whole job is to put
 * it inside the Event Hub tab strip and access gate.
 *
 * That is also why there is no event multi-select above it, even though the Figma draws
 * one: the filtering lives inside the dashboard, and a second set of controls out here
 * would either do nothing or disagree with it.
 *
 * Mounted directly rather than behind a Load button. It was a button while this lived on
 * the debug page, where the point was to prove the Octopus embed round trip on demand;
 * here the dashboard IS the page, and making someone click to see the only thing on it
 * would be a step for its own sake.
 */

import { Container, ContentLayout, Header } from '@amzn/awsui-components-react';
import EventHubTabs from '../../components/EventHubTabs/EventHubTabs';
import QuickSightReport from '../../components/QuickSightReport/QuickSightReport';
import {
  REGISTRATION_ATTENDEE_ACCOUNT,
  REGISTRATION_ATTENDEE_DASHBOARD_ID,
  REGISTRATION_ATTENDEE_SHEET_ID,
} from '../../config/dashboards';
import { useEventHubLogin } from '../../hooks/useEventHubLogin';

export default function LifecycleDataPage() {
  /* The embed is per registered user: Octopus applies its own row-level security from the
     alias, so the dashboard shows what that user is allowed to see.

     The alias is the login Event Hub acts as, not the JWT's — when an admin is viewing as
     someone, this is the one Event Hub request that would otherwise still go out as the
     admin, and the dashboard would show the admin's rows under the other user's banner. It
     is NOT a header the Event Hub service checks: Octopus decides on its own whether a caller
     may request an embed for that alias. */
  const { login } = useEventHubLogin();

  return (
    <ContentLayout
      // Tabs only, matching the landing tab: the active tab already reads "Event
      // Lifecycle Data", so an h1 repeating it verbatim adds nothing.
      header={<EventHubTabs />}
    >
      {/* Named like the Event Detail page's card: the numbers are cut by BU (row-level security
          by the viewer's BU), which the tab name alone does not say. */}
      <Container header={<Header variant="h2">Dashboard by BU</Header>}>
        <QuickSightReport
          dashboardId={REGISTRATION_ATTENDEE_DASHBOARD_ID}
          accountShortName={REGISTRATION_ATTENDEE_ACCOUNT}
          sheetId={REGISTRATION_ATTENDEE_SHEET_ID}
          useRegisteredUser
          userAlias={login}
          height="800"
        />
      </Container>
    </ContentLayout>
  );
}
